import { Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EstFormService, EstFormSaveDetail } from './est-form.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { JsonObjectFactoryService } from '../../../global/services/json-object-factory.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
import { safeEvalFormula } from '../../../shared/_utils/safe-eval-formula';

interface TabDef {
  id: string;
  name: string;
  schedule: 'TI' | 'FSI' | 'OI' | 'GBF' | 'CF';
}

@Component({
  selector: 'gtw-est-form',
  templateUrl: './est-form.component.html',
  styleUrls: ['./est-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class EstFormComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  colData: any = {};
  gridData: any = {};

  modalTitle = 'Estimated Tax';
  minimise = false;
  showFormLoader = true;
  isSaveClicked = false;
  crudLoading = false;

  logged_in_user = '';
  workflow: string | null = null;
  wf: string | null = null;

  readonly tabs: TabDef[] = [
    { id: 'taxable_income',   name: 'Taxable Income',  schedule: 'TI' },
    { id: 'fsi_ftc',          name: 'FSI/FTC',         schedule: 'FSI' },
    { id: 'other_items',      name: 'Other Items',     schedule: 'OI' },
    { id: 'gilti_beat_fdii',  name: 'GILTI BEAT FDII', schedule: 'GBF' },
    { id: 'cash_flow',        name: 'Cash Flow',       schedule: 'CF' },
  ];
  activeTab = 'taxable_income';
  selectedSchedule: 'TI' | 'FSI' | 'OI' | 'GBF' | 'CF' = 'TI';

  allFormData: any[] = [];
  allFormHeader: any[] = [];
  allTableHeader: any[] = [];
  allFormList: any[] = [];
  allTableRows: any[][] = [];
  allFormFormulaList: any[] = [];

  formHeader: any[] = [];
  tableHeader: any[] = [];
  formList: any[] = [];
  tableRows: any[][] = [];
  formFormulaList: any[] = [];

  @Input('col-data')
  set _colData(colData: string) {
    try { this.colData = typeof colData === 'string' ? safeJsonInput<any>(colData, null, 'est-form.parse1') : colData; }
    catch (e) { /* not yet stable */ }
  }

  @Input('grid-data')
  set _gridData(gridData: string) {
    try { this.gridData = typeof gridData === 'string' ? safeJsonInput<any>(gridData, null, 'est-form.parse2') : gridData; }
    catch (e) { /* not yet stable */ }
  }

  @Input('row-data')
  set _rowData(rowData: string) {
    try {
      const data = typeof rowData === 'string' ? safeJsonInput<any>(rowData, null, 'est-form.parse3') : rowData;
      if (data && typeof data === 'object') {
        this.rowData = data;
        this.tryInit();
      }
    } catch (e) { /* not yet stable */ }
  }

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private estFormService: EstFormService,
    private jsonObjectFactory: JsonObjectFactoryService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();

    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    const rowPayload = rowPayloadFromActiveScreen(activeScreen);
    if (rowPayload && Object.keys(rowPayload).length > 0) {
      this.rowData = rowPayload;
    }

    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    if (!this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }

    this.wf = this.rowData?.WORKFLOW || null;
    if (this.wf == 'F') {
      this.workflow = 'Forecast';
    } else if (this.wf == 'A') {
      this.workflow = 'Annualized';
    } else {
      this.workflow = null;
    }

    if (this.workflow && this.rowData?.ME_NAME) {
      this.modalTitle = 'Estimated Tax Projection - ' + this.workflow + ' for ' +
        this.rowData.ME_NAME + ' ( ' + (this.rowData.ME_CODE || '') + ' )';
    } else {
      this.modalTitle = 'Estimated Tax';
    }

    this.loadAllFormData('TI');
    this.loadAllFormulas('TI');
  }

  // ──────── Data loading ────────

  private buildLoadParams(): any {
    return {
      sso_id: this.logged_in_user,
      tax_year: this.filterParams.tax_year || this.globalService?.tax_year,
      out_year: this.filterParams.out_year,
      qtr: this.filterParams.qtr,
      wf_name: this.rowData?.WORKFLOW,
      jcd_key: this.globalService?.jcd_key || this.filterParams.jcd_key,
      combination_key: this.rowData?.COMBINATION_KEY,
      jcd_ta_key: null,
      client_key: this.USER_SETTINGS?.user?.client_key,
      scenarioCode: 'EST',
    };
  }

  private loadAllFormData(defaultSchedule: 'TI' | 'FSI' | 'OI' | 'GBF' | 'CF'): void {
    this.showFormLoader = true;
    this.cdr.detectChanges();

    const params: any = { ...this.buildLoadParams(), action_code: 'bd3szx' };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.processFormData(data?.jsonObject || []);
        this.applySchedule(defaultSchedule);
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      (_err: any) => {
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
    );
  }

  private loadAllFormulas(defaultSchedule: 'TI' | 'FSI' | 'OI' | 'GBF' | 'CF'): void {
    const params: any = {
      action_code: '39bdwl',
      tax_year: this.filterParams.tax_year || this.globalService?.tax_year,
      jcd_key: this.globalService?.jcd_key || this.filterParams.jcd_key,
      wf_name: this.rowData?.WORKFLOW,
      scenarioCode: 'EST',
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.allFormFormulaList = data?.jsonObject || [];
      this.formFormulaList = this.allFormFormulaList.filter((f: any) => f.SCHEDULE_NAME == defaultSchedule);
      this.cdr.detectChanges();
    });
  }

  // ──────── Row grouping (legacy lines 222-296) ────────

  private processFormData(jsonObject: any[]): void {
    this.allFormData = jsonObject || [];

    this.allFormHeader = this.allFormData.filter(o => o.ROW_TYPE === 'FH');
    this.allTableHeader = this.allFormData.filter(o => o.ROW_TYPE === 'TH');

    const withoutFh = this.allFormData.slice(1);
    this.allFormList = withoutFh.filter(o => o.ROW_TYPE !== 'TH');

    this.allTableRows = [];
    let outerList: any[] = [];
    let scheduleName: string | null = null;

    for (let i = 0; i < this.allFormList.length; i++) {
      const item = this.allFormList[i];
      if (item.FORM_LINE_KEY == null) continue;

      scheduleName = item.SCHEDULE_NAME;

      if (scheduleName === 'TI' || scheduleName === 'FSI' ||
          scheduleName === 'OI' || scheduleName === 'GBF') {

        if (item.ATTRIB_ORDER == 1) {
          outerList = [];
          outerList.push(item);
        } else if (outerList.length > 0) {
          const sameLine = outerList.filter(o => o.FORM_LINE_KEY == item.FORM_LINE_KEY);
          if (sameLine.length > 0) {
            outerList.push(item);
          }
        }

        if (item.ROW_TYPE === 'PH' && item.ATTRIB_ORDER == 4) {
          this.allTableRows.push(outerList);
        }
        if (item.ROW_TYPE === 'DT' && item.ATTRIB_ORDER == 3) {
          this.allTableRows.push(outerList);
        }
        if (item.ROW_TYPE === 'AGG' && item.ATTRIB_ORDER == 4) {
          this.allTableRows.push(outerList);
        }

      } else if (scheduleName === 'CF') {
        outerList = [];
        outerList.push(item);
        this.allTableRows.push(outerList);
      }
    }
  }

  private applySchedule(schedule: 'TI' | 'FSI' | 'OI' | 'GBF' | 'CF'): void {
    this.selectedSchedule = schedule;
    this.formHeader = this.allFormHeader.filter(o => o.SCHEDULE_NAME == schedule);
    this.tableHeader = this.allTableHeader.filter(o => o.SCHEDULE_NAME == schedule);
    this.formList = this.allFormList.filter(o => o.SCHEDULE_NAME == schedule);
    this.tableRows = this.allTableRows.filter(group => group?.[0]?.SCHEDULE_NAME == schedule);
    this.formFormulaList = this.allFormFormulaList.filter(f => f.SCHEDULE_NAME == schedule);
  }

  selectTab(tabId: string): void {
    const tab = this.tabs.find(t => t.id === tabId);
    if (!tab) return;
    this.activeTab = tabId;
    this.applySchedule(tab.schedule);
    this.cdr.detectChanges();
  }

  // ──────── Inline editing / formulas (legacy lines 320-506) ────────

  markChange(line: any): void {
    if (!line) return;
    line.IS_CHANGED_FLAG = 'Y';
  }

  applyFormula(line: any): void {
    if (!line) return;
    line.IS_CHANGED_FLAG = 'Y';

    if (line.DATA_TYPE !== 'number') return;

    if (line.SCHEDULE_NAME === 'TI' || line.SCHEDULE_NAME === 'FSI' ||
        line.SCHEDULE_NAME === 'OI' || line.SCHEDULE_NAME === 'GBF') {

      for (const f of this.formFormulaList) {
        const formula = f.ATTRIB_CALC_FORMULA;
        if (formula && formula.trim().length > 0) {
          this.calcAmt(formula, f.LINE_NO, f.LINE_ATTRIB_KEY, line.DATA_TYPE, this.formList);
        }
      }

      const cashFlowFormList = this.allFormList.filter(o => o.SCHEDULE_NAME == 'CF');
      const cashFlowFormulaList = this.allFormFormulaList.filter(o => o.SCHEDULE_NAME == 'CF');

      for (const f of cashFlowFormulaList) {
        const formula = f.ATTRIB_CALC_FORMULA;
        if (formula && formula.trim().length > 0) {
          this.calcCFAmt(formula, f.LINE_NO, f.LINE_ATTRIB_KEY, line.DATA_TYPE, cashFlowFormList);
        }
      }

    } else if (line.SCHEDULE_NAME === 'CF') {
      for (const f of this.formFormulaList) {
        const formula = f.ATTRIB_CALC_FORMULA;
        if (formula && formula.trim().length > 0) {
          this.calcCFAmt(formula, f.LINE_NO, f.LINE_ATTRIB_KEY, line.DATA_TYPE, this.formList);
        }
      }
    }
  }

  /**
   * Port of legacy calcAmt() – substitutes ATTRIB_SHORT_CODE tokens in
   * `formula` with their current ATTRIB_VALUE and evaluates the resulting
   * arithmetic expression, then writes the result back into the matching
   * row.
   */
  private calcAmt(formula: string, line_no: any, attrib_key: any, dataType: any, targetFormList: any[]): void {
    let working = formula;
    const matches = working.match(/\b[^\d\s]+\b/g);
    let lineFormulaArray: string[] | null = null;

    if (matches == null) {
      lineFormulaArray = working.split(/[+\-/\\*\\]/);
    }
    if (!lineFormulaArray) return;

    for (let i = 0; i < lineFormulaArray.length; i++) {
      const token = lineFormulaArray[i].replace(/"/g, '').replace(/'/g, '').replace(/\(|\)/g, '').trim();
      const found = this.formList.find(o => o.ATTRIB_SHORT_CODE == token);
      if (!found) continue;

      const lineAmt = parseFloat(found.ATTRIB_VALUE);
      if (parseFloat(lineAmt as any) >= 0) {
        working = working.replace('(' + token + ')', String(lineAmt));
      } else {
        working = working.replace('(' + token + ')', '(' + lineAmt + ')');
      }
    }

    let evalResult: any;
    try {
      // eslint-disable-next-line no-eval
      evalResult = safeEvalFormula(working, null, 'est-form');
    } catch (_e) {
      return;
    }
    if (evalResult == null || typeof evalResult === 'undefined') return;
    if (parseInt(evalResult, 10) !== parseInt(evalResult, 10)) return;

    const target = targetFormList.find(o => o.LINE_NO == line_no && o.LINE_ATTRIB_KEY == attrib_key);
    if (!target) return;
    const originalValue = target.ATTRIB_VALUE;
    const newValue = Number(evalResult).toFixed();
    target.ATTRIB_VALUE = newValue;
    if (dataType != null && originalValue != newValue) {
      target.IS_CHANGED_FLAG = 'Y';
    }
  }

  /**
   * Port of legacy calcCFAmt() – cash-flow calculations honour quarter-based
   * special multipliers (e.g. EST_A_CF_43_2_MULTI returns 4 / 2 / 1.33 / 1 depending on
   * the active quarter), otherwise behaves like calcAmt() but writes a raw number.
   */
  private calcCFAmt(formula: string, line_no: any, attrib_key: any, dataType: any, targetFormList: any[]): void {
    let working = formula;
    const matches = working.match(/\b[^\d\s]+\b/g);
    let lineFormulaArray: string[] | null = null;

    if (matches == null) {
      lineFormulaArray = working.split(/[+\-/\\*\\]/);
    }
    if (!lineFormulaArray) return;

    const qtr = String(this.filterParams.qtr ?? '');

    for (let i = 0; i < lineFormulaArray.length; i++) {
      const token = lineFormulaArray[i].replace(/"/g, '').replace(/'/g, '').replace(/\(|\)/g, '').trim();
      const found = this.allFormList.find(o => o.ATTRIB_SHORT_CODE == token);
      if (!found) continue;

      let lineAmt: number;
      const shortCode = found.ATTRIB_SHORT_CODE;

      if (shortCode === 'EST_A_CF_43_2_MULTI' || shortCode === 'EST_A_CF_43_4_MULTI') {
        if (qtr === '1' || qtr === '2') lineAmt = 4.0;
        else if (qtr === '3') lineAmt = 2.0;
        else if (qtr === '4') lineAmt = 1.33333;
        else if (qtr === '5') lineAmt = 1.0;
        else lineAmt = parseFloat(found.ATTRIB_VALUE);
      } else if (shortCode === 'EST_A_CF_53_1_MULTI' || shortCode === 'EST_F_CF_26_1_MULTI') {
        if (qtr === '1') lineAmt = 0.25;
        else if (qtr === '2') lineAmt = 0.5;
        else if (qtr === '3') lineAmt = 0.75;
        else if (qtr === '4' || qtr === '5') lineAmt = 1.0;
        else lineAmt = parseFloat(found.ATTRIB_VALUE);
      } else {
        lineAmt = parseFloat(found.ATTRIB_VALUE);
      }

      if (parseFloat(lineAmt as any) >= 0) {
        working = working.replace('(' + token + ')', String(lineAmt));
      } else {
        working = working.replace('(' + token + ')', '(' + lineAmt + ')');
      }
    }

    let evalResult: any;
    try {
      // eslint-disable-next-line no-eval
      evalResult = safeEvalFormula(working, null, 'est-form');
    } catch (_e) {
      return;
    }
    if (evalResult == null || typeof evalResult === 'undefined') return;
    if (parseInt(evalResult, 10) !== parseInt(evalResult, 10)) return;

    const target = targetFormList.find(o => o.LINE_NO == line_no && o.LINE_ATTRIB_KEY == attrib_key);
    if (!target) return;
    const originalValue = target.ATTRIB_VALUE;
    target.ATTRIB_VALUE = evalResult;
    if (dataType != null && originalValue != evalResult) {
      target.IS_CHANGED_FLAG = 'Y';
    }
  }

  // ──────── Save ────────

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const scheduleNames: Array<'TI' | 'FSI' | 'OI' | 'GBF' | 'CF'> = ['TI', 'FSI', 'OI', 'GBF', 'CF'];
    const details: EstFormSaveDetail[] = [];

    for (const sched of scheduleNames) {
      const valueRows = this.allFormList.filter(o =>
        o.SCHEDULE_NAME == sched && o.IS_CHANGED_FLAG === 'Y' && o.ATTRIB_COL === 'b');
      const commentRows = this.allFormList.filter(o =>
        o.SCHEDULE_NAME == sched && o.IS_CHANGED_FLAG === 'Y' && o.ATTRIB_COL === 'd');

      const combined = valueRows.concat(commentRows);
      for (const row of combined) {
        details.push({
          form_name: row.FORM_NAME,
          part_no: row.PART_NO,
          schd_no: row.SCHEDULE_NAME,
          section_name: row.SECTION_NAME,
          row_type: row.ROW_TYPE,
          combination_key: row.COMBINATION_KEY,
          form_key: row.FORM_KEY,
          form_line_key: row.FORM_LINE_KEY,
          line_attrib_key: row.LINE_ATTRIB_KEY,
          attrib_type: row.ATTRIB_TYPE,
          attrib_prop_key: row.ATTRIB_PROP_KEY,
          acct_ref_key: row.ACCT_REF_KEY,
          attrib_value: row.ATTRIB_VALUE,
          trans_details_key: row.TRANS_DETAILS_KEY,
          trans_type_key: row.TRANS_TYPE_KEY,
          is_changed_flag: row.IS_CHANGED_FLAG,
          occurrence: row.OCCURRENCE != null ? parseInt(row.OCCURRENCE, 10) : 1,
        });
      }
    }

    const settings = {
      sso_id: this.logged_in_user,
      screen_key: this.globalService?.screen_key,
      form_name: 'EST_' + (this.wf || ''),
      wf: this.wf,
    };

    this.estFormService.saveForm(this.GENERAL_CONFIG.base_url, {
      details,
      settings,
      globalParams: this.globalService,
      filterParams: this.filterParams,
      combinationKey: this.rowData?.COMBINATION_KEY,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        const errMsg = response?.errorMessage;
        if (response?.callSuccess === '1' && (!errMsg || errMsg === 'null')) {
          this.gtwSnackbarService.openSnackBar('Form Details successfully saved/updated', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(errMsg || 'Error saving form', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (_err: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    );
  }

  reset(): void {
    const tab = this.tabs.find(t => t.id === this.activeTab);
    const schedule = tab?.schedule || 'TI';
    this.loadAllFormData(schedule);
    this.loadAllFormulas(schedule);
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  trackByIndex(index: number): number {
    return index;
  }
}
